// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SentimentDonut from './charts/SentimentDonut';
import CategoryBarChart from './charts/CategoryBarChart';
import VolumeSparkline from './charts/VolumeSparkline';
import InvestmentAreasList from './InsightsDashboard/InvestmentAreasList';
import SubmissionsTable from './InsightsDashboard/SubmissionsTable';
import NarrativeSummary from './InsightsDashboard/NarrativeSummary';
import './InsightsDashboard.css';

const DAY_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '1 year', value: 365 },
];

const CATEGORY_OPTIONS = [
  { label: 'All categories', value: '' },
  { label: 'General', value: 'general' },
  { label: 'Golf', value: 'golf' },
  { label: 'Dining', value: 'dining' },
  { label: 'Amenities', value: 'amenities' },
  { label: 'Membership', value: 'membership' },
  { label: 'Personnel', value: 'personnel' },
];

const SENTIMENT_META = {
  POSITIVE: { label: 'Positive', color: '#2e7d32', bg: '#e8f5e9' },
  NEGATIVE: { label: 'Critical', color: '#c62828', bg: '#ffebee' },
  MIXED: { label: 'Mixed', color: '#f57f17', bg: '#fff8e1' },
  NEUTRAL: { label: 'Neutral', color: '#616161', bg: '#f5f5f5' },
};

export default function InsightsDashboard({ apiBase, tenantConfig, apiHeaders }) {
  const [days, setDays] = useState(30);
  const [category, setCategory] = useState('');
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview | areas | submissions

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ days });
      if (category) params.set('category', category);
      if (selectedCodes.length > 0) params.set('codes', selectedCodes.join(','));
      const res = await axios.get(`${apiBase}/insights?${params}`, { headers: apiHeaders });
      setInsights(res.data);
    } catch (err) {
      setError('Failed to load insights. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [days, category]);

  useEffect(() => {
    load();
  }, [load]);

  const scoreColor =
    insights?.satisfactionScore > 30
      ? 'var(--success)'
      : insights?.satisfactionScore < 0
      ? 'var(--error)'
      : '#f57f17';

  return (
    <div className="dashboard">
      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <div className="dashboard__toolbar">
        <div className="dashboard__toolbar-left">
          <h2 className="dashboard__title">Feedback Insights</h2>
          {insights && !loading && (
            <span className="dashboard__meta">
              {insights.total} submission{insights.total !== 1 ? 's' : ''} · updated {formatRelative(insights.generatedAt)}
            </span>
          )}
        </div>
        <div className="dashboard__filters">
          <label className="dashboard__filter-label" htmlFor="days-filter">Period</label>
          <select
            id="days-filter"
            className="dashboard__select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Select time period"
          >
            {DAY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <label className="dashboard__filter-label" htmlFor="cat-filter">Category</label>
          <select
            id="cat-filter"
            className="dashboard__select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label="Filter by category"
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {insights?.availableAnalysisCodes?.length > 0 && (
            <>
              <label className="dashboard__filter-label" htmlFor="codes-filter">Codes</label>
              <select
                id="codes-filter"
                className="dashboard__select"
                multiple
                value={selectedCodes}
                onChange={(e) => setSelectedCodes(Array.from(e.target.selectedOptions, (o) => o.value))}
                aria-label="Filter by access code"
                style={{ minHeight: 32, maxHeight: 80 }}
              >
                {insights.availableAnalysisCodes.map((ac) => (
                  <option key={ac.code} value={ac.code}>{ac.code} — {ac.label}</option>
                ))}
              </select>
            </>
          )}

          <button
            className="dashboard__refresh-btn"
            onClick={load}
            aria-label="Refresh insights"
            disabled={loading}
          >
            {loading ? <span className="spinner" aria-hidden="true" /> : '↻'}
          </button>
        </div>
      </div>

      {error && (
        <div className="dashboard__error" role="alert">{error}</div>
      )}

      {/* ── Skeleton / content ───────────────────────────────────────── */}
      {loading && !insights ? (
        <div className="dashboard__skeleton" aria-busy="true" aria-label="Loading insights">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton-card" />
          ))}
        </div>
      ) : insights ? (
        <>
          {/* ── KPI strip ───────────────────────────────────────────── */}
          <div className="dashboard__kpis" role="list">
            <div className="kpi-card" role="listitem">
              <span className="kpi-card__value">{insights.total}</span>
              <span className="kpi-card__label">Total Submissions</span>
            </div>
            <div className="kpi-card" role="listitem">
              <span
                className="kpi-card__value"
                style={{ color: insights.satisfactionScore !== null ? scoreColor : 'var(--gray-400)' }}
              >
                {insights.satisfactionScore !== null
                  ? `${insights.satisfactionScore > 0 ? '+' : ''}${insights.satisfactionScore}`
                  : '—'}
              </span>
              <span className="kpi-card__label">Satisfaction Score</span>
              <span className="kpi-card__hint">Positive minus critical, as %</span>
            </div>
            <div className="kpi-card" role="listitem">
              <span className="kpi-card__value">{insights.topInvestmentAreas.length}</span>
              <span className="kpi-card__label">Investment Areas Identified</span>
            </div>
            <div className="kpi-card" role="listitem">
              <span
                className="kpi-card__value"
                style={{ color: SENTIMENT_META[dominantSentiment(insights.sentimentCounts)]?.color }}
              >
                {SENTIMENT_META[dominantSentiment(insights.sentimentCounts)]?.label || '—'}
              </span>
              <span className="kpi-card__label">Dominant Sentiment</span>
            </div>
          </div>

          {/* ── Tabs ─────────────────────────────────────────────────── */}
          <div className="dashboard__tabs" role="tablist" aria-label="Dashboard sections">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'areas', label: `Investment Areas (${insights.topInvestmentAreas.length})` },
              { id: 'submissions', label: 'Submissions' },
            ].map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`dashboard__tab${activeTab === tab.id ? ' dashboard__tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Tab panels ───────────────────────────────────────────── */}
          <div role="tabpanel" aria-label={`${activeTab} panel`}>
            {activeTab === 'overview' && (
              <div className="dashboard__overview-grid">
                {/* Narrative summary — full width at top */}
                <div className="dashboard__card--narrative">
                  <NarrativeSummary narrative={insights.narrative} loading={false} />
                </div>

                {/* Sentiment donut */}
                <div className="dashboard__card dashboard__card--sentiment">
                  <h3 className="dashboard__card-title">Sentiment Distribution</h3>
                  {insights.total > 0 ? (
                    <SentimentDonut counts={insights.sentimentCounts} meta={SENTIMENT_META} />
                  ) : (
                    <EmptyState message="No submissions in this period." />
                  )}
                </div>

                {/* Volume sparkline */}
                <div className="dashboard__card dashboard__card--volume">
                  <h3 className="dashboard__card-title">Submission Volume</h3>
                  {insights.volumeByDay.length > 0 ? (
                    <VolumeSparkline data={insights.volumeByDay} days={days} />
                  ) : (
                    <EmptyState message="No submissions in this period." />
                  )}
                </div>

                {/* Category bar chart */}
                <div className="dashboard__card dashboard__card--categories">
                  <h3 className="dashboard__card-title">Feedback by Category</h3>
                  {insights.total > 0 ? (
                    <CategoryBarChart
                      data={insights.categoryBreakdown}
                      total={insights.total}
                      sentimentMeta={SENTIMENT_META}
                    />
                  ) : (
                    <EmptyState message="No submissions in this period." />
                  )}
                </div>

                {/* Top 5 investment areas preview */}
                <div className="dashboard__card dashboard__card--areas-preview">
                  <h3 className="dashboard__card-title">
                    Top Investment Areas
                    {insights.topInvestmentAreas.length > 5 && (
                      <button
                        className="dashboard__see-all"
                        onClick={() => setActiveTab('areas')}
                      >
                        See all {insights.topInvestmentAreas.length} →
                      </button>
                    )}
                  </h3>
                  {insights.topInvestmentAreas.length > 0 ? (
                    <InvestmentAreasList areas={insights.topInvestmentAreas.slice(0, 5)} compact />
                  ) : (
                    <EmptyState message="No investment areas identified yet." />
                  )}
                </div>
              </div>
            )}

            {activeTab === 'areas' && (
              <div className="dashboard__card">
                <h3 className="dashboard__card-title">
                  All Investment Areas
                  <span className="dashboard__card-count">{insights.topInvestmentAreas.length}</span>
                </h3>
                {insights.topInvestmentAreas.length > 0 ? (
                  <InvestmentAreasList areas={insights.topInvestmentAreas} />
                ) : (
                  <EmptyState message="No investment areas identified in this period." />
                )}
              </div>
            )}

            {activeTab === 'submissions' && (
              <SubmissionsTable
                days={days}
                categoryFilter={category}
                sentimentMeta={SENTIMENT_META}
                apiBase={apiBase}
                apiHeaders={apiHeaders}
              />
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="dashboard__empty">
      <span aria-hidden="true">📭</span>
      <p>{message}</p>
    </div>
  );
}

function dominantSentiment(counts) {
  if (!counts) return 'NEUTRAL';
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'NEUTRAL';
}

function formatRelative(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}
